import { useMemo, useState } from "react";
import { PencilSquareIcon } from "@heroicons/react/24/outline";

import Card from "../shared/Card/Card";
import NotesOutput from "../shared/Notes/NotesOutput";
import PartGradeBadge from "../shared/PartGradeBadge";
import useNotification from "../notifications/useNotifications";
import usePartMutations from "../../data/usePartMutations";
import useFlowTypeAccounts from "../../data/useFlowTypeAccounts";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useJobsByVehicle from "../../data/useJobsByVehicle";
import Modal from "../../layouts/Modal";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import { AccountType } from "../../models/account/IAccount";
import { ResourceType } from "../../models/Note";
import { IPart, PartStatus } from "../../models/Part";
import { IVehicle } from "../../models/Vehicle";
import classNames from "../../utils/classNames";

type DepollutionPartsEditorProps = {
  vehicle: IVehicle;
};

const getPartStatusText = (status: PartStatus | null | undefined) => {
  if (status === null || status === undefined) return "-";
  return PartStatus[status];
};

export default function DepollutionPartsEditor({ vehicle }: DepollutionPartsEditorProps) {
  const isTMEEnabled = CheckFeatureFlag("TME");
  const { patchPart } = usePartMutations();
  const { jobs, createWithPart } = useJobsByVehicle(vehicle.id);
  const { flowTypeAccounts } = useFlowTypeAccounts("DepollutionPartsEditor");
  const { saveHandlers } = useDefaultCRUDHandlers("Part");
  const { saveHandlers: jobSaveHandlers } = useDefaultCRUDHandlers("Job");
  const { addNotification } = useNotification();
  const [savingAccountForPartId, setSavingAccountForPartId] = useState<string | null>(null);
  const [attachingPartId, setAttachingPartId] = useState<string | null>(null);
  const [selectedJobByPartId, setSelectedJobByPartId] = useState<Record<string, string>>({});
  const [notesPart, setNotesPart] = useState<IPart | null>(null);

  const parts = useMemo(
    () => (vehicle.parts ?? []).filter((part): part is IPart => !!part),
    [vehicle.parts]
  );

  const accountOptions = useMemo(
    () => (flowTypeAccounts.data?.data ?? []).map((account) => ({ id: account.id, name: account.name })),
    [flowTypeAccounts.data?.data]
  );
  const jobsForVehicle = jobs.data?.data ?? [];

  const attachedJobByPartId = useMemo(() => {
    const nextMap: Record<string, string> = {};
    for (const job of jobsForVehicle) {
      for (const jobPartCategory of job.jobPartCategories ?? []) {
        if (jobPartCategory.partDataId) {
          nextMap[jobPartCategory.partDataId] = job.id;
        }
      }
    }
    return nextMap;
  }, [jobsForVehicle]);

  const jobOptions = useMemo(
    () => jobsForVehicle.map((job) => ({ id: job.id, name: job.title })),
    [jobsForVehicle]
  );

  const handleAccountChange = (part: IPart, nextAccountId: string) => {
    const accountId = nextAccountId || null;
    if ((part.accountId ?? null) === accountId) return;

    setSavingAccountForPartId(part.id);
    patchPart.mutate(
      {
        id: part.id,
        vehicleId: part.vehicleId,
        accountId,
        updateAccount: true,
      },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
        },
        onError: () => {
          saveHandlers.onError();
          addNotification({
            variant: "error",
            primaryText: "Unable to update account",
          });
        },
        onSettled: () => {
          setSavingAccountForPartId(null);
        },
      }
    );
  };

  const handleAttachToJob = (part: IPart) => {
    const selectedJobId = selectedJobByPartId[part.id] ?? "";
    const existingAttachedJobId = attachedJobByPartId[part.id] ?? "";
    if (selectedJobId && selectedJobId === existingAttachedJobId) {
      addNotification({
        variant: "warn",
        primaryText: "Part already attached to selected job",
      });
      return;
    }

    setAttachingPartId(part.id);
    createWithPart.mutate(
      {
        partId: part.id,
        jobId: selectedJobId || null,
      },
      {
        onSuccess: () => {
          jobSaveHandlers.onSuccess();
        },
        onError: () => {
          jobSaveHandlers.onError();
          addNotification({
            variant: "error",
            primaryText: "Unable to attach part to job",
          });
        },
        onSettled: () => {
          setAttachingPartId(null);
        },
      }
    );
  };

  return (
    <Card title="Vehicle Parts" bodyClassName="p-4">
      <Modal open={!!notesPart} setOpen={(open) => !open && setNotesPart(null)} width="max-w-3xl">
        <div className="space-y-2">
          <h1 className="text-lg font-semibold">Notes</h1>
          <hr />
          {notesPart && (
            <NotesOutput
              notes={notesPart.internalNotes ?? []}
              resourceType={ResourceType.InternalPart}
              resourceId={notesPart.id}
              refetchQuery={["parts", notesPart.id]}
              shouldScroll={false}
            />
          )}
        </div>
      </Modal>

      {parts.length === 0 ? (
        <div className="text-sm text-gray-500">No parts found for this vehicle.</div>
      ) : (
        <div className="space-y-4">
          {parts.map((part) => {
            // Amber when assigned to a non-recycling (customer) flow account — same cue as the jobs screen.
            const assignedAccount = part.accountId
              ? flowTypeAccounts.data?.data?.find((account) => account.id === part.accountId)
              : undefined;
            const isNonRecycleAccount =
              !!part.accountId &&
              !!flowTypeAccounts.data?.data &&
              assignedAccount?.accountType !== AccountType.Recycling;

            return (
            <div
              key={part.id}
              className={classNames(
                "rounded-md border border-gray-200",
                isNonRecycleAccount ? "bg-amber-100" : "bg-white"
              )}
            >
              <div className="grid grid-cols-1 xl:grid-cols-6 gap-3 p-3">
                <div className="xl:col-span-2">
                  <div className="text-sm font-semibold text-gray-900">{part.title ?? "Untitled Part"}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Tag: {part.tagNumber ?? "-"}
                    {part.category?.name ? ` • ${part.category.name}` : ""}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold text-gray-600">Status</div>
                  <div className="text-sm mt-1 text-gray-800">{getPartStatusText(part.partStatus)}</div>
                </div>

                <div>
                  <div className="text-xs font-semibold text-gray-600">Grade</div>
                  <div className="mt-1">
                    <PartGradeBadge part={part} />
                  </div>
                </div>

                {isTMEEnabled && (
                  <div>
                    <div className="text-xs font-semibold text-gray-600">Flow</div>
                    <select
                      className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100"
                      value={part.accountId ?? ""}
                      onChange={(e) => handleAccountChange(part, e.target.value)}
                      disabled={savingAccountForPartId === part.id}
                    >
                      <option value="">Select account</option>
                      {accountOptions.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <div className="text-xs font-semibold text-gray-600">Comment</div>
                  <button
                    type="button"
                    onClick={() => setNotesPart(part)}
                    className="mt-1 inline-flex items-center gap-x-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                  >
                    <PencilSquareIcon className="h-4 w-4 text-green-500" />
                    Notes {part.internalNotes?.length ? `(${part.internalNotes.length})` : ""}
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
